/**
 * Scalar math helpers and the preallocated scratch objects used by hot paths.
 *
 * Non-negotiable #3: zero heap allocations in per-frame / per-tick code. Anything that
 * needs a temporary Vector3/Quaternion/Matrix4 borrows one of the scratch objects below
 * instead of constructing one.
 *
 * Scratch contract (read this before using them):
 *  - A scratch object is valid only until the *next* call that might also use it.
 *  - Never store a reference to a scratch object on a field, in an array, or in a closure.
 *  - A function that borrows scratch must document which slots it clobbers.
 *  - Sim-space quantities are doubles. JS numbers are doubles, and three's Vector3 stores
 *    plain numbers, so Vector3 is double precision here; the FP32 truncation happens only
 *    at the renderer boundary (non-negotiable #4).
 */

import { Euler, Matrix4, Quaternion, Vector2, Vector3 } from 'three';

export const PI = Math.PI;
export const TWO_PI = Math.PI * 2;
export const HALF_PI = Math.PI * 0.5;
export const DEG2RAD = Math.PI / 180;
export const RAD2DEG = 180 / Math.PI;

/** Standard gravity [m/s^2]. */
export const G0 = 9.80665;

/** Clamp `v` to [min, max]. */
export function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

/** Clamp `v` to [0, 1]. */
export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Linear interpolation; `t` is not clamped. */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Where does `v` sit between `a` and `b`? Returns 0 when a === b. */
export function inverseLerp(a: number, b: number, v: number): number {
  const d = b - a;
  return d === 0 ? 0 : (v - a) / d;
}

/** Hermite smoothstep between two edges, clamped to [0, 1]. */
export function smoothstep(edge0: number, edge1: number, v: number): number {
  const t = clamp01(inverseLerp(edge0, edge1, v));
  return t * t * (3 - 2 * t);
}

/** Ken Perlin's smootherstep (C2 continuous), clamped to [0, 1]. */
export function smootherstep(edge0: number, edge1: number, v: number): number {
  const t = clamp01(inverseLerp(edge0, edge1, v));
  return t * t * t * (t * (t * 6 - 15) + 10);
}

/** Step `current` toward `target` by at most `maxDelta`. */
export function moveTowards(current: number, target: number, maxDelta: number): number {
  const d = target - current;
  if (Math.abs(d) <= maxDelta) return target;
  return current + Math.sign(d) * maxDelta;
}

/**
 * Frame-rate independent exponential damping.
 * `halfLife` is the time [s] in which half the remaining distance is covered.
 */
export function expDamp(current: number, target: number, halfLife: number, dt: number): number {
  if (halfLife <= 0) return target;
  const t = 1 - Math.pow(2, -dt / halfLife);
  return current + (target - current) * t;
}

/** Wrap an angle [rad] into (−π, π]. */
export function wrapPi(angle: number): number {
  let a = (angle + PI) % TWO_PI;
  if (a <= 0) a += TWO_PI;
  return a - PI;
}

/** Wrap an angle [rad] into [0, 2π). */
export function wrapTwoPi(angle: number): number {
  const a = angle % TWO_PI;
  return a < 0 ? a + TWO_PI : a;
}

/** Signed shortest difference `to − from` [rad], in (−π, π]. */
export function angleDelta(from: number, to: number): number {
  return wrapPi(to - from);
}

/** Are two numbers equal to within `epsilon`? */
export function approxEquals(a: number, b: number, epsilon = 1e-9): boolean {
  return Math.abs(a - b) <= epsilon;
}

/**
 * Preallocated scratch math objects.
 *
 * Borrow, use, discard — see the scratch contract at the top of this file.
 */
export const scratch = {
  v3a: /* #__PURE__ */ new Vector3(),
  v3b: /* #__PURE__ */ new Vector3(),
  v3c: /* #__PURE__ */ new Vector3(),
  v3d: /* #__PURE__ */ new Vector3(),
  v3e: /* #__PURE__ */ new Vector3(),
  v3f: /* #__PURE__ */ new Vector3(),
  v2a: /* #__PURE__ */ new Vector2(),
  v2b: /* #__PURE__ */ new Vector2(),
  qa: /* #__PURE__ */ new Quaternion(),
  qb: /* #__PURE__ */ new Quaternion(),
  qc: /* #__PURE__ */ new Quaternion(),
  m4a: /* #__PURE__ */ new Matrix4(),
  m4b: /* #__PURE__ */ new Matrix4(),
  ea: /* #__PURE__ */ new Euler(),
} as const;

/** Number of distinct scratch slots — asserted by tests so slots are never silently shared. */
export const SCRATCH_SLOT_COUNT = Object.keys(scratch).length;
